import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormElementGridCellAnswer, Grid, WorksheetElementAnswers, FormElementTypeId } from '@packages/models/api';
import { filterNonBlankAnswers, formatCellInput, getGridCells, getRequiredCellIds, isGridValid } from './utils';
import { useAnswerHandle } from './useAnswerHandle';

export interface GridCell {
    worksheetElementGridId: string;
    subColumnLabel?: string;
    columnLabel?: string;
    columnOrder: number;
    defaultValue: string;
    isRequired: boolean;
}

interface UseGridElementOptions {
    formElementId: string;
    formElementTypeId: FormElementTypeId;
    grid: Grid[] | undefined;
    worksheetGridCellAnswers: FormElementGridCellAnswer[] | undefined;
    required: boolean;
    onAnswerChange?: (answer: WorksheetElementAnswers) => void;
    handleIsValid?: (valid: boolean) => void;
    handleGridCellAnswerChange?: (formElementId: string, gridCellAnswer: FormElementGridCellAnswer[]) => void;
}

export function useGridElement({
    formElementId,
    formElementTypeId,
    grid,
    worksheetGridCellAnswers,
    required,
    onAnswerChange,
    handleIsValid,
    handleGridCellAnswerChange,
}: UseGridElementOptions) {
    const { handleGridAnswer } = useAnswerHandle({ handleGridCellAnswerChange, onAnswerChange });
    const requiredCellIds = useMemo(() => getRequiredCellIds(formElementTypeId, grid ?? []), [formElementTypeId, grid]);
    const elementLevelRequired = required && requiredCellIds.length === 0;

    const initialCellAnswers = useMemo(() => {
        const cells = getGridCells(formElementTypeId, grid ?? []).filter(
            (cell) => cell.worksheetElementGridId
        ) as GridCell[];
        return mergeDefaultValuesWithExistingAnswers(cells, worksheetGridCellAnswers ?? []);
    }, [formElementTypeId, grid, worksheetGridCellAnswers]);

    const [cellAnswers, setCellAnswers] = useState<FormElementGridCellAnswer[]>(initialCellAnswers);
    const [touchedCellIds, setTouchedCellIds] = useState<string[]>([]);

    const previousGridValidationRef = useRef<boolean>(
        isGridValid(elementLevelRequired, requiredCellIds, initialCellAnswers)
    );

    const validateGrid = useCallback(
        (answers: FormElementGridCellAnswer[]) => {
            const valid = isGridValid(elementLevelRequired, requiredCellIds, answers);

            if (valid !== previousGridValidationRef.current) {
                previousGridValidationRef.current = valid;
                handleIsValid?.(valid);
            }
        },
        [elementLevelRequired, requiredCellIds, handleIsValid]
    );

    const isCellInvalid = useCallback(
        (worksheetElementGridId: string): boolean => {
            if (!touchedCellIds.includes(worksheetElementGridId)) {
                return false;
            }

            if (!requiredCellIds.includes(worksheetElementGridId)) {
                return false;
            }

            const answer = cellAnswers.find((answer) => answer.worksheetElementGridId === worksheetElementGridId);
            return !answer?.value?.trim();
        },
        [touchedCellIds, requiredCellIds, cellAnswers]
    );

    useEffect(() => {
        if (formElementTypeId === FormElementTypeId.DynamicGrid) {
            setCellAnswers(initialCellAnswers);
        }
    }, [formElementTypeId, initialCellAnswers]);

    useEffect(() => {
        if (initialCellAnswers.length > 0 && !worksheetGridCellAnswers?.length) {
            handleGridAnswer(formElementId, initialCellAnswers);
        }
    }, []);

    const handleCellChange = useCallback(
        (worksheetElementGridId: string, value: string) => {
            const formattedValue = formatCellInput(formElementTypeId, value);

            setCellAnswers((prev) => updateCellAnswer(prev, worksheetElementGridId, formattedValue));
        },
        [formElementTypeId]
    );

    const handleCellBlur = useCallback(
        (worksheetElementGridId: string) => {
            if (requiredCellIds.includes(worksheetElementGridId)) {
                setTouchedCellIds((prev) =>
                    prev.includes(worksheetElementGridId) ? prev : [...prev, worksheetElementGridId]
                );
            }

            const currentCell = cellAnswers.find((cell) => cell.worksheetElementGridId === worksheetElementGridId);
            const currentValue = currentCell?.value ?? '';
            const trimmedValue = currentValue.trim();

            if (trimmedValue !== currentValue) {
                setCellAnswers((previousCellAnswers) =>
                    updateCellAnswer(previousCellAnswers, worksheetElementGridId, trimmedValue)
                );
            }

            const answersWithTrim =
                trimmedValue !== currentValue
                    ? updateCellAnswer(cellAnswers, worksheetElementGridId, trimmedValue)
                    : cellAnswers;

            handleGridAnswer(formElementId, filterNonBlankAnswers(answersWithTrim));
            validateGrid(answersWithTrim);
        },
        [cellAnswers, requiredCellIds, formElementId, handleGridAnswer, validateGrid]
    );

    const handleCellSelect = useCallback(
        (worksheetElementGridId: string, value: string) => {
            setCellAnswers((previousCellAnswers) => {
                const updated = updateCellAnswer(previousCellAnswers, worksheetElementGridId, value);
                handleGridAnswer(formElementId, filterNonBlankAnswers(updated));
                validateGrid(updated);
                return updated;
            });
        },
        [formElementId, handleGridAnswer, validateGrid]
    );

    return {
        gridCellAnswers: cellAnswers,
        isCellInvalid,
        handleCellChange,
        handleCellBlur,
        handleCellSelect,
    };
}

function mergeDefaultValuesWithExistingAnswers(
    cells: GridCell[],
    existingAnswers: FormElementGridCellAnswer[]
): FormElementGridCellAnswer[] {
    const defaultAnswers: FormElementGridCellAnswer[] = cells
        .filter(
            (cell) =>
                cell.defaultValue &&
                !existingAnswers.some((answer) => answer.worksheetElementGridId === cell.worksheetElementGridId)
        )
        .map((cell) => ({
            worksheetElementGridId: cell.worksheetElementGridId,
            value: cell.defaultValue,
        }));

    return [...existingAnswers, ...defaultAnswers];
}

function updateCellAnswer(
    cellAnswers: FormElementGridCellAnswer[],
    worksheetElementGridId: string,
    value: string
): FormElementGridCellAnswer[] {
    const updatedAnswer: FormElementGridCellAnswer = { worksheetElementGridId, value };
    const exists = cellAnswers.some((cellAnswer) => cellAnswer.worksheetElementGridId === worksheetElementGridId);

    if (exists) {
        return cellAnswers.map((cellAnswer) =>
            cellAnswer.worksheetElementGridId === worksheetElementGridId ? updatedAnswer : cellAnswer
        );
    }

    return [...cellAnswers, updatedAnswer];
}
